import { CHECKOUT_WORDS, cardLabel, money } from "@libs/checkout-core";
import type {
  CardIslandState,
  CheckoutGoods,
  CheckoutSnapshot,
  SavedCard,
} from "@libs/checkout-core";

/** The hold ran out: the tickets are back on sale (the web sheet's words). */
export const EXPIRED_MESSAGE =
  "Your tickets were released when the time ran out. Continue to hold them again.";

/**
 * What the native checkout draws, read from the core's snapshot and the
 * few things only the screen knows (the island is ready, the bank's page
 * is open in it, "You're going" has been seen, a wallet is offered). Pure,
 * so the mapping is tested without drawing anything; the components only
 * draw what this says. The rules are the web checkout's in host mode
 * (apps/ticketing-checkout/src/web/pay-form.tsx and card-entry.tsx).
 */

export type ViewInputs = {
  /** The card island can take a submit. */
  islandReady: boolean;
  /** The bank's page is open inside the island. */
  bankOpen: boolean;
  /** The core said "succeeded": "You're going" has had its moment. */
  succeeded: boolean;
  /** Apple Pay or Google Pay is offered on this device. */
  walletSupported: boolean;
  locale?: string;
};

/** What pressing the primary button does. */
export type PayPress =
  | { kind: "saved"; card: SavedCard }
  | { kind: "card" }
  | { kind: "free" };

export type EndedCopy = { title: string; detail: string };

export type CheckoutView =
  | { screen: "loading" }
  | { screen: "error"; message: string }
  | { screen: "ended"; copy: EndedCopy }
  | { screen: "success" }
  | ({ screen: "pay" } & PayScreen);

export type PayScreen = {
  /** The address the tickets go to, and whether the tick shows. */
  destination: string;
  verified: boolean;
  /** The one place an error is shown. */
  notice: string | null;
  /** The button: its words now, the icon beside them, and whether the
   *  fill has dissolved into plain words (from the fold on). */
  words: string;
  icon: "spinner" | "tick" | null;
  plain: boolean;
  done: boolean;
  disabled: boolean;
  press: PayPress;
  /** Everything but the status folds away. */
  folded: boolean;
  /** The island shows through the fold while the bank's page is open. */
  islandFolded: boolean;
  /** The × on the destination: gone once pressed. */
  canChangeContact: boolean;
  wallets: null | { enabled: boolean };
  /** What the island's own chip and tick are told. */
  island: CardIslandState;
};

type Ended = "refused" | "processing_timeout" | "expired";

/** How a checkout that did not complete ends, in the words for what the
 *  cart sells (the web checkout's, apps/ticketing-checkout outcome.tsx). */
const ENDED: Record<CheckoutGoods, Record<Ended, EndedCopy>> = {
  tickets: {
    refused: {
      title: "We couldn't complete your order",
      detail:
        "The tickets sold out, or the order went past the event's ticket limits, while you paid. Your payment has been refunded in full; it can take a few days to show on your statement.",
    },
    processing_timeout: {
      title: "Payment still processing",
      detail:
        "We're still confirming your payment. Your tickets will be delivered by email when it completes.",
    },
    expired: {
      title: "Your cart expired",
      detail:
        "Your tickets were released when the time ran out. Continue to hold them again.",
    },
  },
  items: {
    refused: {
      title: "We couldn't complete your order",
      detail:
        "This sold out while you paid. Your payment has been refunded in full; it can take a few days to show on your statement.",
    },
    processing_timeout: {
      title: "Payment still processing",
      detail:
        "We're still confirming your payment. Your receipt will be sent by email when it completes.",
    },
    expired: {
      title: "Your cart expired",
      detail: "Nothing was charged. Start again if you still want it.",
    },
  },
};

/** A payment is moving: its press, a wallet sheet or the card being set up. */
export const isPayingNow = (s: CheckoutSnapshot) =>
  s.busy === "card" ||
  s.busy === "lissen" ||
  s.busy === "express" ||
  s.busy === "complete" ||
  s.checkout.kind === "finalizing";

export function checkoutViewOf(
  s: CheckoutSnapshot,
  inputs: ViewInputs,
): CheckoutView {
  const state = s.checkout;
  if (state.kind === "error") return { screen: "error", message: state.message };
  if (state.kind === "loading" || !s.session) return { screen: "loading" };
  if (
    state.kind === "expired" ||
    state.kind === "refused" ||
    state.kind === "processing_timeout"
  )
    return { screen: "ended", copy: ENDED[s.goods][state.kind] };
  // Paid here: the status tells the story to "You're going" first. Paid
  // elsewhere (the hosted page, another device): the ending at once.
  if (state.kind === "success" && (inputs.succeeded || !s.paidHere))
    return { screen: "success" };
  return { screen: "pay", ...payScreenOf(s, inputs) };
}

function payScreenOf(s: CheckoutSnapshot, inputs: ViewInputs): PayScreen {
  const session = s.session!;
  const { pay, busy, zeroTotal } = s;
  const attested = Boolean(s.attested);
  const paying = isPayingNow(s);
  const savedCard = s.cards.find((card) => card.id === s.selected) ?? null;
  const total = `Pay ${money(session.total_minor, session.currency, inputs.locale)}`;
  const words = CHECKOUT_WORDS[s.goods];
  const idle = zeroTotal
    ? busy === "complete"
      ? words.freeBusy
      : words.free
    : savedCard
      ? `${total} with ${cardLabel(savedCard)}`
      : total;
  const press: PayPress = zeroTotal
    ? { kind: "free" }
    : savedCard
      ? { kind: "saved", card: savedCard }
      : { kind: "card" };
  const disabled =
    pay.status !== null ||
    (zeroTotal
      ? !attested || busy !== null
      : !s.canPay || (!savedCard && !inputs.islandReady));
  const folded = pay.phase === "folding";
  return {
    destination: s.destination,
    // The app verified this address before the session was made; the tick
    // stays while the engine confirms it.
    verified: attested || s.awaitingHost,
    notice: s.notice,
    // The bank asking is news worth the button's words.
    words:
      inputs.bankOpen && pay.status !== null
        ? "Confirm with your bank"
        : pay.status
          ? words.payStatus[pay.status]
          : idle,
    // The spinner stays until the tickets are the buyer's; green only then.
    icon:
      pay.status === "done"
        ? "tick"
        : pay.status || (zeroTotal && busy === "complete")
          ? "spinner"
          : null,
    plain: folded,
    done: pay.status === "done",
    disabled,
    press,
    folded,
    islandFolded: folded && !inputs.bankOpen,
    canChangeContact: busy === null && pay.status === null,
    wallets:
      !zeroTotal && s.setup && inputs.walletSupported
        ? { enabled: attested && busy === null }
        : null,
    island: {
      savedCards: s.cards,
      selected: s.selected,
      remember: s.remember,
      // Nothing is payable before the engine attests the buyer.
      locked: !attested,
      disabled: paying,
    },
  };
}
